// src/TestFormPage.js
// Simple test form page for Form Filling Agent workflow testing

import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Paper, Alert, Snackbar } from '@mui/material';

function TestFormPage() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
        phone: '',
        company: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [submissions, setSubmissions] = useState([]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Store submission locally for testing
        const submission = {
            ...formData,
            timestamp: new Date().toISOString()
        };
        setSubmissions([submission, ...submissions]);
        setSubmitted(true);

        // Log to console for debugging
        console.log('[TestFormPage] Form submitted:', submission);

        // Reset form
        setFormData({
            name: '',
            email: '',
            subject: '',
            message: '',
            phone: '',
            company: ''
        });
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            py: 4
        }}>
            <Container maxWidth="md">
                <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
                        🧪 Test Contact Form
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        This form is for testing the Form Filling Agent workflow.
                        Submissions are logged locally and to the console.
                    </Typography>

                    <form onSubmit={handleSubmit} method="POST" action="#">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                name="name"
                                label="Name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                fullWidth
                            />
                            <TextField
                                name="email"
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                fullWidth
                            />
                            <TextField
                                name="phone"
                                label="Phone (optional)"
                                value={formData.phone}
                                onChange={handleChange}
                                fullWidth
                            />
                            <TextField
                                name="company"
                                label="Company (optional)"
                                value={formData.company}
                                onChange={handleChange}
                                fullWidth
                            />
                            <TextField
                                name="subject"
                                label="Subject"
                                value={formData.subject}
                                onChange={handleChange}
                                required
                                fullWidth
                            />
                            <TextField
                                name="message"
                                label="Message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                multiline
                                rows={4}
                                fullWidth
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                sx={{ mt: 2, py: 1.5, fontSize: '1.1rem' }}
                            >
                                Submit
                            </Button>
                        </Box>
                    </form>

                    {/* Show recent submissions for debugging */}
                    {submissions.length > 0 && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                📋 Recent Submissions ({submissions.length})
                            </Typography>
                            {submissions.slice(0, 5).map((sub, idx) => (
                                <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1, bgcolor: '#f5f5f5' }}>
                                    <Typography variant="body2">
                                        <strong>{sub.name}</strong> ({sub.email}) - {sub.subject}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(sub.timestamp).toLocaleString()}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>
                    )}
                </Paper>
            </Container>

            <Snackbar
                open={submitted}
                autoHideDuration={3000}
                onClose={() => setSubmitted(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSubmitted(false)}>
                    Form submitted successfully!
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default TestFormPage;
